import { isoADisplayDDMMYYYYGuiones } from './fechaReporte';
import {
  drawFiltrosSucursalesCajasPdf,
} from './reporteFiltrosEncabezadoExport';

const formatFechaEncabezadoPdf = (iso) =>
  iso ? isoADisplayDDMMYYYYGuiones(String(iso).split('T')[0]) : '—';

function firstNonEmptyString(...vals) {
  for (const v of vals) {
    const s = String(v ?? '').trim();
    if (s) return s;
  }
  return '';
}

/**
 * Encabezado centrado tipo documento (sucursal, responsable, RFC, título en dos líneas, periodo, cajas, turnos).
 * Compatible con reportes que envían `json.encabezado` (nombreComercial, periodoTexto, cajasTexto, turnosTexto, etc.).
 *
 * @param {import('jspdf').jsPDF} doc
 * @param {Record<string, unknown>} encabezado
 * @param {number} pageW
 * @param {number} margin
 * @param {number} y0
 * @param {{
 *   fechaInicio?: string,
 *   fechaFin?: string,
 *   cajaInicio?: number,
 *   cajaFin?: number,
 *   turnoInicio?: number,
 *   turnoFin?: number,
 *   cajaDescripcion?: string,
 *   turnoDescripcion?: string,
 * }} [fallbackForm]
 * @param {{ omitResponsable?: boolean, omitDomicilio?: boolean, tituloResaltadoRgb?: [number, number, number], turnosEncabezadoEstilo?: 'turnos'|'de' }} [encLayout] `turnosEncabezadoEstilo: 'de'` → línea de turnos tipo «De: … a …» (ventas por hora).
 * @returns {number} siguiente posición Y
 */
export function drawEncabezadoReportePdf(
  doc,
  encabezado,
  pageW,
  margin,
  y0,
  fallbackForm = {},
  encLayout = {}
) {
  let y = y0;
  const e = encabezado || {};
  const fb = fallbackForm || {};
  const {
    omitResponsable = false,
    omitDomicilio = false,
    tituloResaltadoRgb = null,
    turnosEncabezadoEstilo = 'turnos',
    preserveReportTitleCase = false,
  } = encLayout || {};
  const turnosEsDe = turnosEncabezadoEstilo === 'de';

  if (
    e.bloqueTexto &&
    String(e.bloqueTexto).trim() &&
    !e.nombreComercial &&
    !e.reporteTitulo
  ) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(15, 23, 42);
    String(e.bloqueTexto)
      .split('\n')
      .forEach((line) => {
        const t = line.trim();
        if (!t) {
          y += 2;
          return;
        }
        doc.splitTextToSize(t, pageW - margin * 2).forEach((ln) => {
          doc.text(ln, pageW / 2, y, { align: 'center' });
          y += 3.6;
        });
      });
    doc.setTextColor(0);
    return y + 6;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  if (e.nombreComercial) {
    doc.text(String(e.nombreComercial).toUpperCase(), pageW / 2, y, { align: 'center' });
    y += 5;
  }

  const responsable = firstNonEmptyString(
    e.responsable,
    e.nombreResponsable,
    e.representanteLegal,
    e.representante,
    e.nombreCompletoUsuario,
    e.nombreUsuario
  );
  if (!omitResponsable && responsable) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(String(responsable).toUpperCase(), pageW / 2, y, { align: 'center' });
    y += 4;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const bloqueDir = omitDomicilio ? [] : [e.domicilioFiscal];
  bloqueDir.forEach((txt) => {
    if (!txt) return;
    const t = String(txt).trim();
    if (!t) return;
    doc.splitTextToSize(t, pageW - margin * 2).forEach((ln) => {
      doc.text(ln, pageW / 2, y, { align: 'center' });
      y += 3.5;
    });
  });
  if (e.rfc) {
    doc.text(`RFC: ${String(e.rfc).toUpperCase()}`, pageW / 2, y, { align: 'center' });
    y += 4;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const titulo =
    e.reporteTitulo || 'REPORTE PRODUCTOS VENDIDOS INCLUYENDO CORTESÍAS';
  const tituloStr = preserveReportTitleCase
    ? String(titulo).trim()
    : String(titulo).toUpperCase();
  if (Array.isArray(tituloResaltadoRgb) && tituloResaltadoRgb.length >= 3) {
    const tw = doc.getTextWidth(tituloStr);
    const pad = 1.8;
    const h = 5.6;
    const bx = pageW / 2 - tw / 2 - pad;
    const by = y - 3.85;
    doc.setFillColor(tituloResaltadoRgb[0], tituloResaltadoRgb[1], tituloResaltadoRgb[2]);
    doc.rect(bx, by, tw + 2 * pad, h, 'F');
    doc.setTextColor(15, 23, 42);
  }
  doc.text(tituloStr, pageW / 2, y, { align: 'center' });
  y += 5;
  if (e.cortesias) {
    doc.setFontSize(9);
    doc.text(String(e.cortesias).toUpperCase(), pageW / 2, y, { align: 'center' });
    y += 5;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  if (e.periodoTexto) {
    doc.text(String(e.periodoTexto), pageW / 2, y, { align: 'center' });
    y += 4;
  } else if (fb.fechaInicio || fb.fechaFin) {
    doc.text(
      `Del: ${formatFechaEncabezadoPdf(fb.fechaInicio)}  al  ${formatFechaEncabezadoPdf(fb.fechaFin)}`,
      pageW / 2,
      y,
      { align: 'center' }
    );
    y += 4;
  }
  y = drawFiltrosSucursalesCajasPdf(doc, e, pageW, margin, y, fb);
  if (e.turnosTexto) {
    const tt = String(e.turnosTexto).trim();
    const line = turnosEsDe
      ? tt.toLowerCase().startsWith('de:')
        ? tt
        : `De: ${tt}`
      : `Turnos: ${tt}`;
    doc.text(line, pageW / 2, y, { align: 'center' });
    y += 4;
  } else if (fb.turnoDescripcion) {
    doc.splitTextToSize(String(fb.turnoDescripcion), pageW - margin * 2).forEach((ln) => {
      doc.text(ln, pageW / 2, y, { align: 'center' });
      y += 3.5;
    });
  } else if (fb.turnoInicio != null || fb.turnoFin != null) {
    const line = turnosEsDe
      ? `De: ${fb.turnoInicio ?? '—'}  a  ${fb.turnoFin ?? '—'}`
      : `Turnos: ${fb.turnoInicio ?? '—'}  a  ${fb.turnoFin ?? '—'}`;
    doc.text(line, pageW / 2, y, { align: 'center' });
    y += 4;
  }
  doc.setTextColor(0);
  return y + 6;
}

/** Ancho interior de tabla en mm (hoja menos márgenes laterales). */
export function pdfTableInnerWidthMm(pageWidthMm, marginMm) {
  return Math.max(1, pageWidthMm - 2 * marginMm);
}

/**
 * Reparte `innerWidthMm` entre columnas en proporción a `weights` para cubrir todo el ancho útil.
 * @param {number[]} weights
 * @param {number} innerWidthMm
 */
export function columnWidthsProportionalMm(weights, innerWidthMm) {
  const w =
    weights.length > 0
      ? weights.map((x) => {
          const n = Number(x);
          return Number.isFinite(n) && n > 0 ? n : 1;
        })
      : [1];
  const sum = w.reduce((a, b) => a + b, 0);
  const inner = Math.max(1, innerWidthMm);
  const out = w.map((x) => (x / sum) * inner);
  const drift = inner - out.reduce((a, b) => a + b, 0);
  if (out.length) out[out.length - 1] += drift;
  return out;
}
